import React, { useState, useEffect } from 'react';
import {
    Server,
    Globe,
    Zap,
    Trash2,
    Plus,
    Unplug,
    Edit2,
    Shield,
    User,
    Hash,
    ShieldCheck,
    Search,
    SlidersHorizontal,
    ArrowUpDown,
    ChevronDown,
    FileText,
    Check,
    AlertCircle
} from 'lucide-react';
import awsLogo from '../../assets/logos/aws-logo.svg';
import gcpLogo from '../../images/gcp-logo.png';
import azureLogo from '../../assets/logos/azure-logo.svg';
import onPremiseLogo from '../../assets/logos/on-premises-logo.svg';
import './DMSConnections.css';

import { API_BASE_URL } from '../../config';

const AnimatedCounter = ({ value, duration = 1000 }) => {
    const [count, setCount] = useState(0);
    
    useEffect(() => {
        let startTime = null;
        const startValue = count;
        const endValue = value;
        const diff = endValue - startValue;
        
        if (diff === 0) return;
        
        const step = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            const easeProgress = progress * (2 - progress);
            setCount(Math.floor(startValue + diff * easeProgress));
            if (progress < 1) {
                window.requestAnimationFrame(step);
            } else {
                setCount(endValue);
            }
        };
        window.requestAnimationFrame(step);
    }, [value, duration]);
    
    return <span>{count}</span>;
};

const DMSConnections = ({ connections, setShowConnectionModal, onEdit, onRefresh, onDeleteConnection, setNotify, currentTab = 'active', setCurrentTab }) => {
    const [testingConnection, setTestingConnection] = useState(null);

    // Auto-refresh polling every 60s when this component is mounted
    useEffect(() => {
        const intervalId = setInterval(() => {
            if (typeof onRefresh === 'function') {
                onRefresh();
            }
        }, 60000);
        return () => clearInterval(intervalId);
    }, [onRefresh]);


    // Transition state
    const [isTransitioning, setIsTransitioning] = useState(false);

    // Toolbar States
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [sortBy, setSortBy] = useState('recently_created');
    const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
    const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);

    // Close dropdowns on outside clicks
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.toolbar-dropdown-container')) {
                setIsFilterDropdownOpen(false);
                setIsSortDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleTestConnection = async (connectionId) => {
        setTestingConnection(connectionId);
        try {
            const connection = connections.find(c => c.id === connectionId);
            if (!connection) return;

            const response = await fetch(`${API_BASE_URL}/api/connections/test`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...connection,
                    port: parseInt(connection.port, 10)
                })
            });

            const data = await response.json();
            if (data.success) {
                if (setNotify) setNotify({ type: 'success', message: `Test for "${connection.name}" passed!` });
            } else {
                const errorMessage = data.details ? `${data.error}: ${data.details}` : data.error;
                if (setNotify) setNotify({ type: 'error', message: `Test failed: ${errorMessage}` });
            }
            if (typeof onRefresh === 'function') onRefresh();
        } catch (error) {
            if (setNotify) setNotify({ type: 'error', message: 'Network error during test' });
        } finally {
            setTestingConnection(null);
        }
    };

    const handleDeleteClick = (connection) => {
        if (typeof onDeleteConnection === 'function') {
            onDeleteConnection(connection);
        }
    };

    const handleTabChange = (tab) => {
        if (tab === currentTab) return;
        setIsTransitioning(true);
        setTimeout(() => {
            setCurrentTab(tab);
            setSearchQuery('');
            setSelectedFilter('all');
            setIsTransitioning(false);
        }, 300);
    };

    // Helper check for drafts
    const isDraftConnection = (conn) => {
        if (!conn.options) return false;
        try {
            const opts = typeof conn.options === 'string' ? JSON.parse(conn.options) : conn.options;
            return opts && (opts.is_draft === true || opts.isDraft === true);
        } catch (e) {
            return false;
        }
    };

    // Separate Active vs Draft Connections
    const activeConnections = connections ? connections.filter(conn => !isDraftConnection(conn)) : [];
    const draftConnections = connections ? connections.filter(conn => isDraftConnection(conn)) : [];

    // Filter & Sort Connections
    const getFilteredAndSorted = (list, isDraftsList) => {
        let result = [...list];

        // 1. Search Query Filter
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(conn => {
                const status = conn.health_status || 'unknown';
                const isPrivate = conn.connectivity_mode === 'private';
                return (
                    conn.name?.toLowerCase().includes(q) ||
                    conn.host?.toLowerCase().includes(q) ||
                    conn.database_engine?.toLowerCase().includes(q) ||
                    conn.type?.toLowerCase().includes(q) ||
                    conn.cloud_provider?.toLowerCase().includes(q) ||
                    (isPrivate ? 'private' : 'public').includes(q) ||
                    status.includes(q) || (status === 'connected' && 'success'.includes(q))
                );
            });
        }

        // 2. Dropdown Category Filter
        if (selectedFilter !== 'all') {
            result = result.filter(conn => {
                const status = conn.health_status || 'unknown';
                switch (selectedFilter) {
                    case 'connected':
                        return !isDraftsList && status === 'connected';
                    case 'failed':
                        return !isDraftsList && status === 'failed';
                    case 'private':
                        return conn.connectivity_mode === 'private';
                    case 'public':
                        return conn.connectivity_mode === 'public';
                    case 'aws':
                        return conn.cloud_provider?.toLowerCase() === 'aws';
                    case 'gcp':
                        return conn.cloud_provider?.toLowerCase() === 'gcp';
                    case 'incomplete':
                        return isDraftsList && (!conn.host || !conn.username || !conn.database_engine);
                    default:
                        return true;
                }
            });
        }

        // 3. Sorting Logic
        result.sort((a, b) => {
            if (sortBy === 'a_z') {
                return (a.name || '').localeCompare(b.name || '');
            } else if (sortBy === 'recently_created') {
                const getCreateTime = (item) => {
                    if (item.created_at) return new Date(item.created_at).getTime();
                    if (item.id && String(item.id).startsWith('dms_draft_')) {
                        const parts = String(item.id).split('_');
                        const ts = parseInt(parts[parts.length - 1], 10);
                        if (!isNaN(ts)) return ts;
                    }
                    return 0;
                };
                return getCreateTime(b) - getCreateTime(a);
            } else if (sortBy === 'recently_updated') {
                return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
            } else if (sortBy === 'status') {
                const statusA = a.health_status || 'unknown';
                const statusB = b.health_status || 'unknown';
                const weight = { connected: 3, failed: 2, unknown: 1 };
                return (weight[statusB] || 0) - (weight[statusA] || 0);
            }
            return 0;
        });

        return result;
    };

    const displayActiveConnections = getFilteredAndSorted(activeConnections, false);
    const displayDraftConnections = getFilteredAndSorted(draftConnections, true);

    // Calculate metrics for the header
    const stats = {
        total: activeConnections.length,
        verified: activeConnections.filter(c => c.health_status === 'connected').length,
        failed: activeConnections.filter(c => c.health_status === 'failed').length
    };

    const filterOptions = currentTab === 'active'
        ? [
            { value: 'all', label: 'All Connections' },
            { value: 'connected', label: 'Connected' },
            { value: 'failed', label: 'Failed' },
            { value: 'private', label: 'Private Network' },
            { value: 'public', label: 'Public Internet' },
            { value: 'aws', label: 'AWS Cloud' },
            { value: 'gcp', label: 'GCP Cloud' }
        ]
        : [
            { value: 'all', label: 'All Drafts' },
            { value: 'private', label: 'Private Network' },
            { value: 'public', label: 'Public Internet' },
            { value: 'aws', label: 'AWS Cloud' },
            { value: 'gcp', label: 'GCP Cloud' },
            { value: 'incomplete', label: 'Incomplete Drafts' }
        ];

    const sortOptions = currentTab === 'active'
        ? [
            { value: 'recently_created', label: 'Recently Created' },
            { value: 'recently_updated', label: 'Recently Updated' },
            { value: 'a_z', label: 'Alphabetical (A-Z)' },
            { value: 'status', label: 'Sort by Status' }
        ]
        : [
            { value: 'recently_created', label: 'Recently Created' },
            { value: 'recently_updated', label: 'Recently Updated' },
            { value: 'a_z', label: 'Alphabetical (A-Z)' }
        ];

    const activeFilterLabel = filterOptions.find(o => o.value === selectedFilter)?.label || 'Filter';
    const activeSortLabel = sortOptions.find(o => o.value === sortBy)?.label || 'Sort';

    return (
        <div className="dms-page-container dms-connections-container">
            <div className="dms-page-header">
                <div className="header-title-stack">
                    <h2>Database Connections</h2>
                    <p className="header-subtitle">Manage and monitor your enterprise database ecosystem and connectivity bridges.</p>
                </div>
                <div className="header-actions-premium">
                    <div className="stats-summary-bar">
                        <div className="stat-pill">
                            <span className="stat-label">Total</span>
                            <span className="stat-count"><AnimatedCounter value={stats.total} /></span>
                        </div>
                        <div className="stat-divider"></div>
                        <div className="stat-pill verified">
                            <span className="stat-label">Verified</span>
                            <span className="stat-count"><AnimatedCounter value={stats.verified} /></span>
                        </div>
                        <div className="stat-pill failed">
                            <span className="stat-label">Failed</span>
                            <span className="stat-count"><AnimatedCounter value={stats.failed} /></span>
                        </div>
                    </div>
                    <button
                        className="btn-add-nexus"
                        onClick={() => setShowConnectionModal(true)}
                    >
                        <Plus size={18} />
                        <span>Add Connection</span>
                    </button>
                </div>
            </div>

            {/* Toolbar: Search, Filter, Sort + Toggles */}
            <div className="connections-toolbar-premium">
                <div className="search-bar-glass-container">
                    <Search size={16} className="search-icon-dim" />
                    <input
                        type="text"
                        className="search-input-glass"
                        placeholder={currentTab === 'active' ? "Search connections..." : "Search drafts..."}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button className="clear-search-btn" onClick={() => setSearchQuery('')}>&times;</button>
                    )}
                </div>

                <div className="view-switcher-premium">
                    <button
                        className={`btn-switcher-tab ${currentTab === 'active' ? 'active' : ''}`}
                        onClick={() => handleTabChange('active')}
                    >
                        Connections
                    </button>
                    <button
                        className={`btn-switcher-tab ${currentTab === 'drafts' ? 'active' : ''} ${draftConnections.length > 0 ? 'has-drafts' : ''}`}
                        onClick={() => handleTabChange('drafts')}
                    >
                        Drafts {draftConnections.length > 0 ? `• ${draftConnections.length}` : ''}
                    </button>
                </div>

                <div className="toolbar-actions-group">
                    {/* Filter Dropdown */}
                    <div className="toolbar-dropdown-container">
                        <button
                            className={`btn-toolbar-dropdown ${selectedFilter !== 'all' ? 'active-filter' : ''}`}
                            onClick={() => {
                                setIsFilterDropdownOpen(!isFilterDropdownOpen);
                                setIsSortDropdownOpen(false);
                            }}
                        >
                            <SlidersHorizontal size={13} />
                            <span>{activeFilterLabel}</span>
                            <ChevronDown size={13} className={`chevron-rotate ${isFilterDropdownOpen ? 'open' : ''}`} />
                        </button>

                        <div className={`dropdown-panel-glass float-menu ${isFilterDropdownOpen ? 'open' : ''}`}>
                            <div className="dropdown-panel-header">Filter by</div>
                            <div className="dropdown-panel-options">
                                {filterOptions.map(opt => (
                                    <button
                                        key={opt.value}
                                        className={`dropdown-option-btn ${selectedFilter === opt.value ? 'selected' : ''}`}
                                        onClick={() => {
                                            setSelectedFilter(opt.value);
                                            setIsFilterDropdownOpen(false);
                                        }}
                                    >
                                        <span>{opt.label}</span>
                                        {selectedFilter === opt.value && <Check size={13} className="check-icon-purple" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Sort Dropdown */}
                    <div className="toolbar-dropdown-container">
                        <button
                            className="btn-toolbar-dropdown"
                            onClick={() => {
                                setIsSortDropdownOpen(!isSortDropdownOpen);
                                setIsFilterDropdownOpen(false);
                            }}
                        >
                            <ArrowUpDown size={13} />
                            <span>{activeSortLabel}</span>
                            <ChevronDown size={13} className={`chevron-rotate ${isSortDropdownOpen ? 'open' : ''}`} />
                        </button>

                        <div className={`dropdown-panel-glass float-menu ${isSortDropdownOpen ? 'open' : ''}`}>
                            <div className="dropdown-panel-header">Sort by</div>
                            <div className="dropdown-panel-options">
                                {sortOptions.map(opt => (
                                    <button
                                        key={opt.value}
                                        className={`dropdown-option-btn ${sortBy === opt.value ? 'selected' : ''}`}
                                        onClick={() => {
                                            setSortBy(opt.value);
                                            setIsSortDropdownOpen(false);
                                        }}
                                    >
                                        <span>{opt.label}</span>
                                        {sortBy === opt.value && <Check size={13} className="check-icon-purple" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Area Grid */}
            <div className={`dms-connections-grid ${isTransitioning ? 'transition-blur' : ''}`}>
                {currentTab === 'active' ? (
                    activeConnections.length > 0 ? (
                        displayActiveConnections.length > 0 ? (
                            displayActiveConnections.map((connection, index) => (
                                <div
                                    key={connection.id}
                                    className={`dms-conn-card role-${connection.role || 'source'} ${connection.health_status === 'connected' ? 'success' : connection.health_status || 'unknown'} ${testingConnection === connection.id ? 'checking' : ''}`}
                                    style={{
                                        animationDelay: `${index * 0.08}s`,
                                        visibility: 'visible'
                                    }}
                                >
                                    <div className="card-top-row">
                                        <div className={`role-badge-pill ${connection.role || 'source'}`}>
                                            {connection.role?.toUpperCase() || 'SOURCE'}
                                        </div>
                                        <div className="connectivity-chips-group">
                                            <div className="connectivity-label">
                                                {connection.connectivity_mode === 'public' ? (
                                                    <><Globe size={11} /> PUBLIC</>
                                                ) : (
                                                    <><Shield size={11} /> PRIVATE</>
                                                )}
                                            </div>
                                            <div className={`status-badge-pill ${testingConnection === connection.id ? 'checking' : (connection.health_status === 'connected' ? 'success' : connection.health_status || 'unknown')}`}>
                                                {testingConnection === connection.id ? 'CHECKING...' : 
                                                 connection.health_status === 'connected' ? 'CONNECTED' : 
                                                 connection.health_status === 'failed' ? 'FAILED' : 'AWAITING'}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card-identity-header">
                                        <div className="provider-avatar-glass">
                                            <ProviderLogo provider={connection.cloud_provider} size={36} />
                                        </div>
                                        <div className="title-stack-content">
                                            <div className="primary-title-row">
                                                <h3>{connection.name}</h3>
                                            </div>
                                            <div className="subtitle-detail">
                                                {connection.database_engine || "Amazon RDS for PostgreSQL"}
                                            </div>
                                            <div className="inline-badges">
                                                {connection.region && (
                                                    <span className="region-pill-min">{connection.region.toUpperCase()}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="actions-row-strip">
                                        <button
                                            className="btn-sq-action test"
                                            onClick={() => handleTestConnection(connection.id)}
                                            disabled={testingConnection === connection.id}
                                        >
                                            <Zap size={14} className={testingConnection === connection.id ? "spin-smooth" : ""} />
                                            <span>TEST</span>
                                        </button>
                                        <button
                                            className="btn-sq-action edit"
                                            onClick={() => onEdit && onEdit(connection)}
                                        >
                                            <Edit2 size={14} />
                                            <span>EDIT</span>
                                        </button>
                                        <button
                                            className="btn-sq-action delete"
                                            onClick={() => handleDeleteClick(connection)}
                                        >
                                            <Trash2 size={14} />
                                            <span>DELETE</span>
                                        </button>
                                    </div>

                                    <div className="inner-data-slate">
                                        <div className="slate-item-box full">
                                            <div className="slate-label-row">
                                                <Server size={12} className="slate-item-icon" />
                                                <span className="slate-label">Host Endpoint</span>
                                            </div>
                                            <span className="slate-value" title={connection.host}>{connection.host}</span>
                                        </div>
                                        <div className="slate-grid-row tri">
                                            <div className="slate-item-box">
                                                <div className="slate-label-row">
                                                    <Hash size={12} className="slate-item-icon" />
                                                    <span className="slate-label">Port</span>
                                                </div>
                                                <span className="slate-value">{connection.port || 5432}</span>
                                            </div>
                                            <div className="slate-item-box">
                                                <div className="slate-label-row">
                                                    <User size={12} className="slate-item-icon" />
                                                    <span className="slate-label">User</span>
                                                </div>
                                                <span className="slate-value">{connection.username}</span>
                                            </div>
                                            <div className="slate-item-box">
                                                <div className="slate-label-row">
                                                    {connection.ssl_enabled ?
                                                        <ShieldCheck size={12} className="slate-item-icon secure" /> :
                                                        <Shield size={12} className="slate-item-icon" />
                                                    }
                                                    <span className="slate-label">Security</span>
                                                </div>
                                                <span className={`slate-value ${connection.ssl_enabled ? 'secure-text' : ''}`}>
                                                    {connection.ssl_enabled ? 'SSL Secure' : 'Standard'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card-footer-mini">
                                        <div className="footer-meta-right">
                                            <span className="date-stamp-mini">
                                                {connection.created_at ? new Date(connection.created_at).toLocaleDateString() : new Date().toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-search-results-glass">
                                <SlidersHorizontal size={40} className="no-results-icon" />
                                <h3>No matching connections</h3>
                                <p>We couldn't find any active database connections matching your query or selected filters.</p>
                                <button className="btn-secondary-toolbar" onClick={() => {
                                    setSearchQuery('');
                                    setSelectedFilter('all');
                                }}>
                                    Reset Filters
                                </button>
                            </div>
                        )
                    ) : (
                        <div className="empty-state-card">
                            <div className="empty-state-icon">
                                <Unplug size={48} className="spin-slow" />
                            </div>
                            <h3>No database connections yet</h3>
                            <p>
                                Add your source and destination databases to start building your migration pipelines.
                            </p>
                            <button
                                className="btn-primary"
                                onClick={() => setShowConnectionModal(true)}
                                style={{ padding: '0.85rem 2.5rem', marginTop: '1rem' }}
                            >
                                <Plus size={20} />
                                Create First Connection
                            </button>
                        </div>
                    )
                ) : (
                    draftConnections.length > 0 ? (
                        displayDraftConnections.length > 0 ? (
                            displayDraftConnections.map((conn, index) => (
                                <DraftCard
                                    key={conn.id}
                                    conn={conn}
                                    index={index}
                                    onEdit={onEdit}
                                    onDelete={onDeleteConnection}
                                />
                            ))
                        ) : (
                            <div className="no-search-results-glass">
                                <SlidersHorizontal size={40} className="no-results-icon" />
                                <h3>No matching drafts</h3>
                                <p>We couldn't find any draft connections matching your query or selected filters.</p>
                                <button className="btn-secondary-toolbar" onClick={() => {
                                    setSearchQuery('');
                                    setSelectedFilter('all');
                                }}>
                                    Reset Filters
                                </button>
                            </div>
                        )
                    ) : (
                        <div className="empty-state-card">
                            <div className="empty-state-icon">
                                <FileText size={48} style={{ color: '#fbbf24' }} />
                            </div>
                            <h3>No draft connections</h3>
                            <p>
                                When you start configuring a connection and leave the form incomplete, it will be automatically auto-saved here.
                            </p>
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default DMSConnections;

// ── Global Helper Components & Functions (Defined outside to prevent React unmount/remount loop) ──

const ProviderLogo = ({ provider, size = 18, className = "" }) => {
    const p = provider?.toLowerCase();
    if (p === 'aws') return <img src={awsLogo} alt="AWS" width={Math.round(size * 1.6)} height={size} className={`${className} aws-logo-img aws-logo-theme-fix`} style={{ objectFit: 'contain' }} />;
    if (p === 'gcp') return <img src={gcpLogo} alt="GCP" width={size} height={size} className={className} style={{ objectFit: 'contain' }} />;
    if (p === 'azure') return <img src={azureLogo} alt="Azure" width={size} height={size} className={className} style={{ objectFit: 'contain' }} />;
    if (p === 'on-premise') return <img src={onPremiseLogo} alt="On-Premise" width={size} height={size} className={className} style={{ objectFit: 'contain' }} />;
    return <Globe size={size} />;
};

const getDraftProgress = (conn) => {
    let score = 0;
    let total = 5;
    if (conn.host) score++;
    if (conn.port) score++;
    if (conn.username) score++;
    if (conn.database_engine || conn.type) score++;
    if (conn.role) score++;
    const percent = Math.round((score / total) * 100);
    return { score, total, percent };
};

const getRelativeTime = (dateStr) => {
    if (!dateStr) return 'Just now';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hrs < 24) return `${hrs}h ago`;
    return `${days}d ago`;
};

const DraftCard = ({ conn, index, onEdit, onDelete }) => {
    const incomplete = !conn.host || !conn.username || !conn.database_engine;
    const autoSaved = conn.options && (typeof conn.options === 'string' ? JSON.parse(conn.options) : conn.options).auto_saved === true;
    const { percent } = getDraftProgress(conn);

    return (
        <div
            className={`draft-conn-card role-${conn.role || 'source'}`}
            style={{ animationDelay: `${index * 0.08}s` }}
        >
            <div className="card-top-row">
                <div className={`role-badge-pill ${conn.role || 'source'}`}>
                    {conn.role?.toUpperCase() || 'SOURCE'}
                </div>
                <div className="draft-badges-group">
                    {autoSaved && (
                        <span className="badge-auto-saved">
                            <Zap size={9} /> Auto Saved
                        </span>
                    )}
                    {incomplete && (
                        <span className="badge-incomplete">
                            <AlertCircle size={9} /> Incomplete
                        </span>
                    )}
                </div>
            </div>

            <div className="card-identity-header">
                <div className="provider-avatar-glass draft-avatar">
                    <ProviderLogo provider={conn.cloud_provider} size={36} />
                </div>
                <div className="title-stack-content">
                    <div className="primary-title-row">
                        <h3>{conn.name || 'Untitled Draft'}</h3>
                    </div>
                    <div className="subtitle-detail">
                        {conn.database_engine || (conn.type ? conn.type.toUpperCase() : 'Database Connection')}
                    </div>
                    <div className="inline-badges">
                        {conn.region && (
                            <span className="region-pill-min">{conn.region.toUpperCase()}</span>
                        )}
                        <span className="draft-status-tag">DRAFT</span>
                    </div>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="draft-progress-container">
                <div className="draft-progress-label-row">
                    <span className="draft-progress-text">Configuration Progress</span>
                    <span className="draft-progress-percent">{percent}%</span>
                </div>
                <div className="draft-progress-track">
                    <div className="draft-progress-fill" style={{ width: `${percent}%` }}></div>
                </div>
            </div>

            <div className="actions-row-strip draft-actions">
                <button
                    className="btn-sq-action test resume"
                    onClick={() => onEdit && onEdit(conn)}
                >
                    <Zap size={14} />
                    <span>EDIT DRAFT</span>
                </button>
                <button
                    className="btn-sq-action delete"
                    onClick={() => onDelete && onDelete(conn)}
                >
                    <Trash2 size={14} />
                    <span>DELETE</span>
                </button>
            </div>

            <div className="inner-data-slate draft-slate">
                <div className="slate-item-box full">
                    <div className="slate-label-row">
                        <Server size={12} className="slate-item-icon" />
                        <span className="slate-label">Host Endpoint</span>
                    </div>
                    <span className={`slate-value ${!conn.host ? 'slate-empty' : ''}`} title={conn.host}>
                        {conn.host || '— not configured —'}
                    </span>
                </div>
                <div className="slate-grid-row tri">
                    <div className="slate-item-box">
                        <div className="slate-label-row">
                            <Hash size={12} className="slate-item-icon" />
                            <span className="slate-label">Port</span>
                        </div>
                        <span className={`slate-value ${!conn.port ? 'slate-empty' : ''}`}>
                            {conn.port || '—'}
                        </span>
                    </div>
                    <div className="slate-item-box">
                        <div className="slate-label-row">
                            <User size={12} className="slate-item-icon" />
                            <span className="slate-label">User</span>
                        </div>
                        <span className={`slate-value ${!conn.username ? 'slate-empty' : ''}`}>
                            {conn.username || '—'}
                        </span>
                    </div>
                    <div className="slate-item-box">
                        <div className="slate-label-row">
                            {conn.ssl_enabled ?
                                <ShieldCheck size={12} className="slate-item-icon secure" /> :
                                <Shield size={12} className="slate-item-icon" />
                            }
                            <span className="slate-label">SSL</span>
                        </div>
                        <span className="slate-value">
                            {conn.ssl_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                    </div>
                </div>
            </div>

            <div className="card-footer-mini">
                <div className="footer-meta-right">
                    <span className="date-stamp-mini">
                        {getRelativeTime(conn.updated_at || conn.created_at)}
                    </span>
                </div>
            </div>
        </div>
    );
};
